import { closeWindow, isInClient } from './liff'

type Base = { name: string; label: string; required?: boolean; hint?: string }

/** กรองตัวอักษรระหว่างพิมพ์ + ตอน submit (เช่น อนุญาตเฉพาะไทย/อังกฤษ) */
export type Sanitize = (raw: string) => string

export type Field =
  | (Base & { kind: 'text' | 'tel'; placeholder?: string; sanitize?: Sanitize; value?: string; readonly?: boolean })
  | (Base & { kind: 'password'; placeholder?: string })
  | (Base & { kind: 'date'; placeholder?: string })
  | (Base & { kind: 'textarea'; placeholder?: string })
  | (Base & { kind: 'select'; options: string[]; value?: string })
  | (Base & { kind: 'radio'; options: string[]; allowOther?: boolean })
  | (Base & { kind: 'checkbox'; text: string })
  | { name: string; label: string; kind: 'file'; accept?: string; hint?: string }

export interface FormSpec {
  title: string
  intro?: string
  fields: Field[]
  submitLabel?: string
  submit: (values: Record<string, string>, files: Record<string, File>) => Promise<SuccessView>
}

export interface SuccessView {
  title: string
  lines: string[]
}

export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!))
}

export function renderForm(root: HTMLElement, spec: FormSpec): void {
  root.innerHTML = `
    <header class="hd">
      <h1>${esc(spec.title)}</h1>
      ${spec.intro ? `<p>${esc(spec.intro)}</p>` : ''}
    </header>
    <form class="card" novalidate>
      ${spec.fields.map(fieldHtml).join('')}
      <p class="err" hidden></p>
      <button type="submit" class="btn">${esc(spec.submitLabel ?? 'ส่ง')}</button>
    </form>`

  const form = root.querySelector('form') as HTMLFormElement
  const err = root.querySelector('.err') as HTMLElement
  const btn = form.querySelector('button') as HTMLButtonElement

  const fail = (msg: string, el?: { focus(): void } | null): void => {
    err.textContent = msg
    err.hidden = false
    el?.focus()
  }

  // กรองตัวอักษรระหว่างพิมพ์ (เช่น ไทยเท่านั้น / อังกฤษเท่านั้น)
  for (const f of spec.fields) {
    if ((f.kind === 'text' || f.kind === 'tel') && f.sanitize) {
      const el = form.elements.namedItem(f.name) as HTMLInputElement | null
      el?.addEventListener('input', () => {
        const clean = f.sanitize!(el.value)
        if (clean !== el.value) el.value = clean
      })
    }
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    err.hidden = true

    const values: Record<string, string> = {}
    const files: Record<string, File> = {}

    for (const f of spec.fields) {
      if (f.kind === 'file') {
        const input = form.elements.namedItem(f.name) as HTMLInputElement | null
        const file = input?.files?.[0]
        if (file) files[f.name] = file
        continue
      }

      if (f.kind === 'checkbox') {
        const input = form.elements.namedItem(f.name) as HTMLInputElement | null
        const checked = !!input?.checked
        values[f.name] = checked ? 'ใช่' : ''
        if (f.required && !checked) return fail(`กรุณายืนยัน: ${f.label}`, input)
        continue
      }

      if (f.kind === 'radio') {
        const nodes = form.elements.namedItem(f.name) as RadioNodeList | null
        let value = (nodes?.value ?? '').trim()
        if (value === '__other__') {
          const other = form.elements.namedItem(`${f.name}__other`) as HTMLInputElement | null
          value = (other?.value ?? '').trim()
          if (f.required && !value) return fail(`กรุณาระบุ: ${f.label} (อื่น ๆ)`, other)
        }
        values[f.name] = value
        if (f.required && !value) return fail(`กรุณาเลือก: ${f.label}`)
        continue
      }

      const input = form.elements.namedItem(f.name) as
        | HTMLInputElement
        | HTMLTextAreaElement
        | HTMLSelectElement
        | null
      let value = (input?.value ?? '').trim()
      if ((f.kind === 'text' || f.kind === 'tel') && f.sanitize) value = f.sanitize(value)
      values[f.name] = value
      if (f.required && !value) return fail(`กรุณากรอก: ${f.label}`, input)
    }

    btn.disabled = true
    btn.textContent = 'กำลังส่ง…'
    try {
      const result = await spec.submit(values, files)
      renderSuccess(root, result)
    } catch (ex) {
      fail(ex instanceof Error ? ex.message : String(ex))
      btn.disabled = false
      btn.textContent = spec.submitLabel ?? 'ส่ง'
    }
  })
}

function hintHtml(f: { hint?: string }): string {
  return f.hint ? `<small class="hint">${esc(f.hint)}</small>` : ''
}

function fieldHtml(f: Field): string {
  const req = 'required' in f && f.required ? ' <span class="req">*</span>' : ''

  if (f.kind === 'radio') {
    const opts = f.options
      .map((o) => `<label class="opt"><input type="radio" name="${f.name}" value="${esc(o)}"><span>${esc(o)}</span></label>`)
      .join('')
    const other = f.allowOther
      ? `<label class="opt"><input type="radio" name="${f.name}" value="__other__"><span>อื่น ๆ</span></label>
         <input type="text" name="${f.name}__other" class="opt-other" placeholder="โปรดระบุ">`
      : ''
    return `<fieldset class="fld"><legend>${esc(f.label)}${req}</legend>${hintHtml(f)}${opts}${other}</fieldset>`
  }

  if (f.kind === 'checkbox') {
    return `<label class="fld chk"><input type="checkbox" name="${f.name}"><span>${esc(f.text)}${req}</span></label>`
  }

  const head = `<label class="fld"><span>${esc(f.label)}${req}</span>${hintHtml(f)}`
  switch (f.kind) {
    case 'textarea':
      return `${head}<textarea name="${f.name}" rows="4" placeholder="${esc(f.placeholder ?? '')}"></textarea></label>`
    case 'select':
      return `${head}<select name="${f.name}"><option value="">— เลือก —</option>${f.options
        .map((o) => `<option${o === f.value ? ' selected' : ''}>${esc(o)}</option>`)
        .join('')}</select></label>`
    case 'file':
      return `${head}<input type="file" name="${f.name}" accept="${esc(f.accept ?? 'image/*')}" capture="environment"></label>`
    case 'password':
      return `${head}<input type="password" name="${f.name}" autocomplete="off" placeholder="${esc(f.placeholder ?? '')}"></label>`
    default: {
      const type = f.kind === 'tel' ? 'tel' : f.kind === 'date' ? 'date' : 'text'
      const extra =
        (f.kind === 'text' || f.kind === 'tel') && f.readonly
          ? ` readonly value="${esc(f.value ?? '')}"`
          : (f.kind === 'text' || f.kind === 'tel') && f.value
            ? ` value="${esc(f.value)}"`
            : ''
      return `${head}<input type="${type}" name="${f.name}" placeholder="${esc(f.placeholder ?? '')}"${extra}></label>`
    }
  }
}

export function renderSuccess(root: HTMLElement, view: SuccessView): void {
  const inClient = isInClient()
  root.innerHTML = `
    <div class="done">
      <div class="check" aria-hidden="true">✓</div>
      <h1>${esc(view.title)}</h1>
      ${view.lines.map((l) => `<p>${esc(l)}</p>`).join('')}
      ${inClient ? `<button class="btn" id="close">ปิดหน้าต่าง</button>` : `<p class="hint">กดปุ่ม ✕ มุมขวาบนเพื่อปิด</p>`}
    </div>`
  root.querySelector('#close')?.addEventListener('click', () => closeWindow())
}

export function renderError(root: HTMLElement, message: string): void {
  root.innerHTML = `<div class="screen-error"><p>${esc(message)}</p></div>`
}
